import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Bot, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { CURRENCIES } from "../lib/currency";

const TONES = [
  { v: "friendly", l: "Friendly & warm" },
  { v: "professional", l: "Professional & formal" },
  { v: "cheeky", l: "Cheeky & fun" },
  { v: "calm", l: "Calm & reassuring" },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [form, setForm] = useState({ name: "", description: "", pricing_notes: "", ava_tone: "friendly", phone: "", area: "", display_currency: "GBP" });
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  let signupIntent = "paid";
  try { signupIntent = localStorage.getItem("td_signup_intent") || "paid"; } catch {}
  const wantTrial = signupIntent === "trial";

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.name.trim() || !form.description.trim()) { toast.error("Company name and a description of your business are required"); return; }
    if (!agreed) { toast.error("Please accept the Terms & Conditions to continue"); return; }
    setSubmitting(true);
    try {
      await api.post("/company", { ...form, accept_terms: true, want_trial: wantTrial });
      await checkAuth();
      try { localStorage.removeItem("td_signup_intent"); } catch {}
      toast.success("Company created — welcome aboard!");
      navigate(wantTrial ? "/dashboard" : "/dashboard/billing");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create company");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground grain flex items-center justify-center p-5" style={{ paddingTop: "calc(1.25rem + env(safe-area-inset-top))" }}>
      <div className="w-full max-w-xl">
        <div className="flex items-center gap-2.5 mb-8">
          <div className="h-9 w-9 bg-primary rounded-sm flex items-center justify-center"><Bot className="h-5 w-5 text-white" /></div>
          <span className="font-display text-2xl tracking-wide">TRADIE<span className="text-primary">DESK</span></span>
        </div>

        <h1 className="font-display text-4xl md:text-5xl tracking-tight mb-2">SET UP YOUR<br /><span className="text-primary">RECEPTIONIST.</span></h1>
        <p className="text-muted-foreground mb-8">Tell Ava about your business so she answers exactly right for you. Hi {user?.name?.split(" ")[0]}.</p>

        <div className="bg-[#18181b] border border-white/10 rounded-sm p-6 space-y-5" data-testid="onboarding-form">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Company Name *</Label>
            <Input data-testid="company-name-input" value={form.name} onChange={set("name")} placeholder="e.g. Bright Spark Electrics" className="rounded-sm bg-[#0f0f11] border-white/10" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Describe your business & services *</Label>
            <Textarea data-testid="description-input" value={form.description} onChange={set("description")} placeholder="Explain what your business does in as much detail as you like — the services you offer, the kind of jobs you take on, what makes you different. Ava uses this (plus her own knowledge of your industry) to answer customers and ask the right questions." className="rounded-sm bg-[#0f0f11] border-white/10 min-h-[160px]" />
            <p className="text-xs text-muted-foreground mt-1.5">The more detail you give, the smarter Ava is. She'll also draw on general knowledge of your industry to ask the right questions.</p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">How you price (optional, plain English)</Label>
            <Textarea data-testid="pricing-notes-input" value={form.pricing_notes} onChange={set("pricing_notes")} placeholder={"Just explain how you price, like you would to a customer. e.g. Window spraying is usually £80 a window, up to £300 for a big bay. Doors £180–£350 depending on style. Kitchens vary: small £850–£1,300, large £1,850–£4,000 depending on surfaces and repairs. Access/scaffold and travel can affect the price."} className="rounded-sm bg-[#0f0f11] border-white/10 min-h-[120px]" />
            <p className="text-xs text-muted-foreground mt-1.5">Ava reads this and gives customers a rough ballpark — always saying the exact price is confirmed on your callback. You can refine it any time under <span className="text-foreground">My Prices</span>.</p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Ava's tone of voice</Label>
            <Select value={form.ava_tone} onValueChange={(v) => setForm({ ...form, ava_tone: v })}>
              <SelectTrigger data-testid="tone-select" className="rounded-sm bg-[#0f0f11] border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#18181b] border-white/10 text-foreground">
                {TONES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1.5">How Ava comes across to your customers. You can change this any time in Settings.</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Phone</Label>
              <Input data-testid="phone-input" value={form.phone} onChange={set("phone")} placeholder="0800 123 456" className="rounded-sm bg-[#0f0f11] border-white/10" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Service Area</Label>
              <Input data-testid="area-input" value={form.area} onChange={set("area")} placeholder="e.g. North London" className="rounded-sm bg-[#0f0f11] border-white/10" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Preferred currency</Label>
            <Select value={form.display_currency} onValueChange={(v) => setForm({ ...form, display_currency: v })}>
              <SelectTrigger data-testid="currency-select" className="rounded-sm bg-[#0f0f11] border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#18181b] border-white/10 text-foreground">
                {CURRENCIES.map((c) => <SelectItem key={c.code} value={c.code} data-testid={`currency-opt-${c.code}`}>{c.symbol} {c.label} ({c.code})</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1.5">Prices are shown in this currency as a guide. Plans are priced in <span className="text-foreground">£GBP</span>; at checkout Stripe shows the exact amount in your local currency. You can change this any time on the Billing page.</p>
          </div>
          <Button data-testid="onboarding-submit" onClick={submit} disabled={submitting} className="w-full rounded-sm bg-primary hover:bg-orange-600 text-white font-semibold h-11 gap-2">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Launch My Receptionist <ArrowRight className="h-4 w-4" /></>}
          </Button>
          <label className="flex items-start gap-2.5 cursor-pointer select-none" data-testid="terms-checkbox-label">
            <input type="checkbox" data-testid="accept-terms-checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-0.5 h-4 w-4 accent-primary shrink-0" />
            <span className="text-xs text-muted-foreground leading-relaxed">
              I have read and accept the <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary underline" data-testid="terms-link">Terms &amp; Conditions</a>. {wantTrial
                ? "The free trial takes no payment and no card details — payment is only taken if you choose to subscribe (plans from £45.99/month), and the free trial is one-time use."
                : "You'll be asked to subscribe (plans from £45.99/month) via Stripe — payment is only taken when you choose to subscribe."}
            </span>
          </label>
          <p className="text-xs text-muted-foreground text-center">{wantTrial ? "1-day free trial — no card or payment details needed. You're only charged if you choose to subscribe." : "You'll be asked to subscribe (plans from £45.99/month) to activate Ava — no free trial on this plan."}</p>
        </div>
      </div>
    </div>
  );
}
